import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { RxDashboard } from "react-icons/rx";
import { FiLogOut, FiMoon, FiSun, FiCalendar, FiUsers, FiBook } from "react-icons/fi";
import { FaChevronDown } from "react-icons/fa";
import { AiOutlineMenuFold, AiOutlineMenuUnfold } from "react-icons/ai";
import { motion, AnimatePresence } from "framer-motion";
import { MdSchool, MdAssignment, MdAssessment } from "react-icons/md";

const Sidebar = ({ isCollapsed, setIsCollapsed, darkMode, setDarkMode }) => {
  const [openMenu, setOpenMenu] = React.useState("");
  const navigate = useNavigate();

  const linkClass = (isActive) =>
    classNames(
      "flex items-center font-medium rounded-xl transition-all duration-200 group",
      {
        "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/25":
          isActive,
        "text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800":
          !isActive,
      },
      isCollapsed ? "justify-center p-3" : "gap-3 py-3 px-3"
    );

  const toggleMenu = (menu) => {
    setOpenMenu((prev) => (prev === menu ? "" : menu));
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  // Get user info from localStorage
  const userName = localStorage.getItem("full_name") || "Faculty";
  const userEmail = localStorage.getItem("email") || "";

  return (
    <div
      className={classNames(
        "bg-white dark:bg-gray-900 h-screen sticky top-0 flex flex-col shadow-xl transition-all duration-300 overflow-hidden border-r border-gray-200 dark:border-gray-700",
        { "w-80": !isCollapsed, "w-20": isCollapsed }
      )}
    >
      {/* Header */}
      <div
        className={classNames(
          "flex items-center border-b border-gray-200 dark:border-gray-700",
          isCollapsed ? "p-4 flex-col gap-4" : "p-6 justify-between"
        )}
      >
        {!isCollapsed ? (
          <>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                <MdSchool className="text-white text-xl" />
              </div>
              <div>
                <span className="font-bold text-lg text-gray-900 dark:text-white block">
                  GMGC
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Attendance System
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400"
                title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {darkMode ? <FiSun className="text-yellow-400" /> : <FiMoon />}
              </button>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400"
                title="Collapse Sidebar"
              >
                <AiOutlineMenuFold />
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
              <MdSchool className="text-white text-xl" />
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400"
                title={darkMode ? "Light Mode" : "Dark Mode"}
              >
                {darkMode ? <FiSun className="text-yellow-400" /> : <FiMoon />}
              </button>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400"
                title="Expand Sidebar"
              >
                <AiOutlineMenuUnfold />
              </button>
            </div>
          </>
        )}
      </div>

      {/* User Info - Only show when expanded */}
      {/* {!isCollapsed && (
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
              {userName.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-white truncate">
                {userName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {userEmail}
              </p>
            </div>
          </div>
        </div>
      )} */}

      {/* Navigation */}
      <nav
        className={classNames(
          "flex flex-col gap-2 flex-1 overflow-y-auto py-4",
          isCollapsed ? "px-2" : "px-4"
        )}
      >
        {/* Dashboard */}
        <NavLink
          to="/dashboard"
          className={({ isActive }) => linkClass(isActive)}
          title="Dashboard"
        >
          {({ isActive }) => (
            <>
              <div className={classNames(
                "p-2 rounded-lg transition-all duration-200",
                isActive 
                  ? "bg-white/20" 
                  : "bg-gray-100 dark:bg-gray-800 group-hover:bg-gray-200 dark:group-hover:bg-gray-700"
              )}>
                <RxDashboard className={classNames(
                  "text-xl transition-colors duration-200",
                  isActive 
                    ? "text-white" 
                    : "text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400"
                )} />
              </div>
              {!isCollapsed && (
                <span className={classNames(
                  "transition-colors duration-200",
                  isActive 
                    ? "text-white" 
                    : "text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400"
                )}>
                  Dashboard
                </span>
              )}
            </>
          )}
        </NavLink>
{/* Student Management */}
        <SidebarGroup
          title="User Management"
          icon={<FiUsers className="text-gray-600 dark:text-gray-400" />}
          menuKey="User Management"
          isCollapsed={isCollapsed}
          openMenu={openMenu}
          toggleMenu={toggleMenu}
        >
          <NavLink
            to="/dashboard/students"
            className={({ isActive }) => classNames(
              "flex items-center gap-3 p-3 rounded-lg transition-all duration-200 text-sm",
              isActive 
                ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md" 
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-purple-600 dark:hover:text-purple-400"
            )}
          >
            {({ isActive }) => (
              <>
                <FiUsers className="text-lg" />
                Student List
              </>
            )}
          </NavLink>
          <NavLink
            to="/dashboard/teacher-list"
            className={({ isActive }) => classNames(
              "flex items-center gap-3 p-3 rounded-lg transition-all duration-200 text-sm",
              isActive 
                ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md" 
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-purple-600 dark:hover:text-purple-400"
            )}
          >
            {({ isActive }) => (
              <>
                <FiCalendar className="text-lg" />
                Teacher List
              </>
            )}
          </NavLink>
        </SidebarGroup>

        {/* Attendance Management */}
        <SidebarGroup
          title="Attendance"
          icon={<FiCalendar className="text-gray-600 dark:text-gray-400" />}
          menuKey="attendance"
          isCollapsed={isCollapsed}
          openMenu={openMenu}
          toggleMenu={toggleMenu}
        >
          <NavLink
            to="/dashboard/attendance"
            className={({ isActive }) => classNames(
              "flex items-center gap-3 p-3 rounded-lg transition-all duration-200 text-sm",
              isActive 
                ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md" 
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-purple-600 dark:hover:text-purple-400"
            )}
          >
            {({ isActive }) => (
              <>
                <MdAssignment className="text-lg" />
                Attendance
              </>
            )}
          </NavLink>
          <NavLink
            to="/dashboard/paper-wise-Reports"
            className={({ isActive }) => classNames(
              "flex items-center gap-3 p-3 rounded-lg transition-all duration-200 text-sm",
              isActive 
                ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md" 
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-purple-600 dark:hover:text-purple-400"
            )}
          >
            {({ isActive }) => (
              <>
                <MdAssessment className="text-lg" />
                Paper wise Reports
              </>
            )}
          </NavLink>
          <NavLink
            to="/dashboard/semester-wise-Reports"
            className={({ isActive }) => classNames(
              "flex items-center gap-3 p-3 rounded-lg transition-all duration-200 text-sm",
              isActive 
                ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md" 
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-purple-600 dark:hover:text-purple-400"
            )}
          >
            {({ isActive }) => (
              <>
                <MdAssessment className="text-lg" />
                Semester wise Reports
              </>
            )}
          </NavLink>
          <NavLink
            to="/dashboard/department-wise-Reports"
            className={({ isActive }) => classNames(
              "flex items-center gap-3 p-3 rounded-lg transition-all duration-200 text-sm",
              isActive 
                ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md" 
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-purple-600 dark:hover:text-purple-400"
            )}
          >
            {({ isActive }) => (
              <>
                <MdAssessment className="text-lg" />
                Department wise Reports
              </>
            )}
          </NavLink>
        </SidebarGroup>

        
       
      </nav>

      {/* Footer */}
      <div className={classNames("p-4 border-t border-gray-200 dark:border-gray-700", isCollapsed ? "text-center" : "")}>
        {/* College Info - Only when expanded */}
        {!isCollapsed && (
          <div className="mb-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold">
              Gokhale Memorial Girls' College
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              ESTD 1938 • Kolkata
            </p>
          </div>
        )}

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="flex items-center justify-center gap-3 w-full p-3 rounded-xl text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:shadow-md transition-all duration-200"
          title="Logout"
        >
          <FiLogOut />
          {!isCollapsed && "Logout"}
        </button>
      </div>
    </div>
  );
};

const SidebarGroup = ({
  title,
  icon,
  menuKey,
  isCollapsed,
  openMenu,
  toggleMenu,
  children,
}) => (
  <div>
    <button
      onClick={() => toggleMenu(menuKey)}
      className={classNames(
        "flex items-center justify-between w-full p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 group",
        isCollapsed ? "justify-center" : ""
      )}
      title={isCollapsed ? title : ""}
    >
      <div className="flex items-center gap-3">
        <div className={classNames(
          "p-2 rounded-lg transition-all duration-200",
          openMenu === menuKey 
            ? "bg-purple-100 dark:bg-purple-900/30" 
            : "bg-gray-100 dark:bg-gray-800 group-hover:bg-gray-200 dark:group-hover:bg-gray-700"
        )}>
          {React.cloneElement(icon, {
            className: classNames(
              "text-xl transition-colors duration-200",
              openMenu === menuKey 
                ? "text-purple-600 dark:text-purple-400" 
                : "text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400"
            )
          })}
        </div>
        {!isCollapsed && (
          <span className={classNames(
            "font-medium transition-colors duration-200",
            openMenu === menuKey 
              ? "text-purple-600 dark:text-purple-400" 
              : "text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400"
          )}>
            {title}
          </span>
        )}
      </div>
      {!isCollapsed && (
        <FaChevronDown
          className={classNames(
            "transition-all duration-200",
            openMenu === menuKey 
              ? "rotate-180 text-purple-600 dark:text-purple-400" 
              : "text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400"
          )}
        />
      )}
    </button>

    <AnimatePresence>
      {openMenu === menuKey && !isCollapsed && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="ml-10 mt-1 flex flex-col gap-1 overflow-hidden"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default Sidebar;